/**
 * Role keys (matches prototype convention):
 *   'super' — Super Admin (IT, one person)
 *   'admin' — Admin (Suchita, Gazal)
 *   'tl'    — Team Leader
 *   'tm'    — Team Member
 */

export const ROLES = Object.freeze({
    SUPER: 'super',
    ADMIN: 'admin',
    TL: 'tl',
    TM: 'tm',
  });
  
  export const ROLE_LABELS = Object.freeze({
    super: 'Super Admin',
    admin: 'Admin',
    tl: 'Team Leader',
    tm: 'Team Member',
  });
  
  export const ROLE_COLORS = Object.freeze({
    super: '#EC4899',
    admin: '#4F46E5',
    tl: '#0891B2',
    tm: '#6B7280',
  });
  
  /* ============================================================
     Permission helpers — guard UI and route access.
     Server-side enforcement still lives in Supabase RLS policies.
     ============================================================ */
  
  /** Reports page is Admin + Super Admin only (BIT 14) */
  export function canViewReports(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER;
  }
  
  /** Who can assign tasks to others:
   *  - Super: anyone
   *  - Admin: anyone
   *  - TL:    own team only (caller must enforce dept scope)
   *  - TM:    only self (handled separately as "add task for yourself") */
  export function canAssignTask(role) {
    return role === ROLES.SUPER || role === ROLES.ADMIN || role === ROLES.TL;
  }
  
  /** TL and above can add Remarks on tasks (extended from Admin-only BIT 3
   *  per product decision: TLs annotate their team's work directly). */
  export function canAddRemarks(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER || role === ROLES.TL;
  }

  /** TL and above can delete tasks. RLS scopes the actual rows:
   *  Admin/Super delete anything, TL deletes within their department. */
  export function canDeleteTask(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER || role === ROLES.TL;
  }

  /** TL and above can override the auto-detected standard time. */
  export function canOverrideStdTime(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER || role === ROLES.TL;
  }

  /** Knowledge base authoring — Admin and Super only. */
  export function canManageKnowledgeBase(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER;
  }
  
  /** Reopen Done tasks (status → Redo)
   *  - Admin reopens TL tasks
   *  - TL reopens TM tasks
   *  - Super can do anything
   *  Caller must also verify hierarchy relative to the task owner. */
  export function canReopenTask(role) {
    return role === ROLES.SUPER || role === ROLES.ADMIN || role === ROLES.TL;
  }
  
  /** Approve registrations / change roles — Super Admin only (BIT 3) */
  export function canApproveRegistrations(role) {
    return role === ROLES.SUPER;
  }
  
  /** Doubts inbox visibility — Admin + Super (BIT 9) */
  export function canViewDoubtsInbox(role) {
    return role === ROLES.ADMIN || role === ROLES.SUPER;
  }
  
  /** Raise a doubt — only the task owner (any role) can raise.
   *  This helper checks "does the role qualify at all"; caller must
   *  also confirm the user is the task's owner. */
  export function canRaiseDoubt(role) {
    return [ROLES.SUPER, ROLES.ADMIN, ROLES.TL, ROLES.TM].includes(role);
  }
  
  /** Where the user lands after login (BIT 5).
   *  TM lands on Job Sheet (their own tasks).
   *  Everyone else lands on Dashboard. */
  export function getDashboardRoute(role) {
    switch (role) {
      case ROLES.SUPER:
        return '/super-admin/dashboard';
      case ROLES.ADMIN:
        return '/admin/dashboard';
      case ROLES.TL:
        return '/tl/dashboard';
      case ROLES.TM:
        return '/member/job-sheet';
      default:
        return '/login';
    }
  }
  
  /** Get the path prefix for a role's section (e.g. '/admin', '/tl') */
  export function getRolePrefix(role) {
    switch (role) {
      case ROLES.SUPER: return '/super-admin';
      case ROLES.ADMIN: return '/admin';
      case ROLES.TL:    return '/tl';
      case ROLES.TM:    return '/member';
      default:          return '/';
    }
  }

  /** Task detail page route, scoped to the user's role prefix */
  export function getTaskRoute(role, taskId) {
    return `${getRolePrefix(role)}/task/${taskId}`;
  }
  
  /* ============================================================
     Quick role-type checks (for component conditionals)
     ============================================================ */
  export const isSuperAdmin = (role) => role === ROLES.SUPER;
  export const isAdmin = (role) => role === ROLES.ADMIN;
  export const isTL = (role) => role === ROLES.TL;
  export const isTM = (role) => role === ROLES.TM;
  
  /** Combined: admin or super (very common check) */
  export const isAdminOrSuper = (role) => role === ROLES.ADMIN || role === ROLES.SUPER;
  
  /** Does role A "outrank" role B? Used for reopen / oversight checks. */
  const RANK = { super: 4, admin: 3, tl: 2, tm: 1 };
  export function outranks(roleA, roleB) {
    return (RANK[roleA] || 0) > (RANK[roleB] || 0);
  }
  
  /** Human-readable role label */
  export function roleLabel(role) {
    return ROLE_LABELS[role] || 'Unknown';
  }